import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertCircle, X, User, Mail, Phone, MapPin, Briefcase,
  GraduationCap, Award, Globe, FileText, ChevronDown,
  ChevronUp, RefreshCw, LogIn, CheckCircle, Lightbulb
} from 'lucide-react';

interface CVConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateProfile: () => void;
  onJustLogin: () => void;
  userExists: boolean;
  extractedData: {
    name: string;
    email: string;
    phone?: string;
    title?: string;
    location?: string;
    summary?: string;
    skills?: string[];
    experience?: Array<{
      company: string;
      position: string;
      start_date?: string;
      end_date?: string;
      description?: string;
    }>;
    education?: Array<{
      institution: string;
      degree?: string;
      field_of_study?: string;
      start_date?: string;
      end_date?: string;
    }>;
    languages?: Array<{
      language: string;
      proficiency?: string;
    }>;
    certifications?: Array<{
      name: string;
      issuer?: string;
      date?: string;
    }>;
  };
}

const CVConfirmationModal: React.FC<CVConfirmationModalProps> = ({
  isOpen,
  onClose,
  onUpdateProfile,
  onJustLogin,
  userExists,
  extractedData
}) => {
  const [expandedSection, setExpandedSection] = useState<string | null>('basic');
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const toggleSection = (section: string) => {
    // If clicking the currently expanded section, collapse it
    // Otherwise, expand the clicked section (collapsing others automatically)
    const newSection = expandedSection === section ? null : section;
    setExpandedSection(newSection);

    // Immediate smooth scroll (no delay, just slow smooth motion)
    if (newSection) {
      setTimeout(() => {
        const element = sectionRefs.current[newSection];
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 50); // Tiny delay to let state update, then smooth scroll starts immediately
    }
  };

  const CollapsibleSection: React.FC<{
    title: string;
    icon: React.ReactNode;
    sectionKey: string;
    children: React.ReactNode;
    count?: number;
  }> = ({ title, icon, sectionKey, children, count }) => {
    const isExpanded = expandedSection === sectionKey;

    return (
      <div
        ref={(el) => (sectionRefs.current[sectionKey] = el)}
        id={`section-${sectionKey}`}
        className="border border-white/10 rounded-xl overflow-hidden backdrop-blur-sm bg-gray-900/70"
      >
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full p-3 bg-gray-900/60 hover:bg-gray-900/80 transition-all duration-500 ease-out flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <div className="text-amber-400">{icon}</div>
            <span className="font-medium text-gray-200">{title}</span>
            {count && <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-1 rounded-full border border-amber-500/30">
              {count}
            </span>}
          </div>
          <div className="text-gray-400">{isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</div>
        </button>

        <div
          className="overflow-hidden transition-all duration-700 ease-in-out"
          style={{
            maxHeight: isExpanded ? '2000px' : '0px',
            opacity: isExpanded ? 1 : 0,
          }}
        >
          <div className="p-3 bg-gray-900/70 border-t border-white/10">
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - Matches styling guide */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-2xl z-50"
            onClick={onClose}
          />

          {/* Modal - Glassmorphic design */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 40 }}
            transition={{ type: "spring", bounce: 0.3, duration: 0.4 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gray-900/40 backdrop-blur-3xl border border-white/20 rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
              {/* Header - Dark background */}
              <div className="bg-gray-900/95 backdrop-blur-xl border-b border-white/10 p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Icon without background */}
                    {userExists ? <AlertCircle className="w-8 h-8 text-amber-400" /> : <CheckCircle className="w-8 h-8 text-green-400" />}
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {userExists ? 'Account Found' : 'CV Processed Successfully'}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {userExists ? 'Review extracted information' : 'Review your information before creating account'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors duration-500 ease-out"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* Body - flexible height with scroll */}
              <div className="p-6 space-y-4 flex-1 overflow-y-auto bg-gray-900/50">
                <p className="text-gray-200 mb-4 text-sm">
                  We've extracted the following information from your CV. Please review and choose how to proceed:
                </p>
                <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl backdrop-blur-sm">
                  <p className="text-amber-200 text-sm flex items-start gap-2">
                    <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>Tip:</strong> Expand sections below to review details, then scroll down to see action buttons.</span>
                  </p>
                </div>

                {/* Basic Information */}
                <CollapsibleSection
                  title="Basic Information"
                  icon={<User className="w-4 h-4" />}
                  sectionKey="basic"
                >
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-300 font-medium">{extractedData.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-300">{extractedData.email}</span>
                    </div>
                    {extractedData.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-300">{extractedData.phone}</span>
                      </div>
                    )}
                    {extractedData.title && (
                      <div className="flex items-center gap-3">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-300">{extractedData.title}</span>
                      </div>
                    )}
                    {extractedData.location && (
                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-300">{extractedData.location}</span>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>

                {/* Professional Summary */}
                {extractedData.summary && (
                  <CollapsibleSection
                    title="Professional Summary"
                    icon={<FileText className="w-4 h-4" />}
                    sectionKey="summary"
                  >
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {extractedData.summary}
                    </p>
                  </CollapsibleSection>
                )}

                {/* Skills */}
                {extractedData.skills && extractedData.skills.length > 0 && (
                  <CollapsibleSection
                    title="Skills"
                    icon={<Award className="w-4 h-4" />}
                    sectionKey="skills"
                    count={extractedData.skills.length}
                  >
                    <div className="flex flex-wrap gap-2">
                      {extractedData.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full text-sm border border-amber-500/30"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Experience */}
                {extractedData.experience && extractedData.experience.length > 0 && (
                  <CollapsibleSection
                    title="Work Experience"
                    icon={<Briefcase className="w-4 h-4" />}
                    sectionKey="experience"
                    count={extractedData.experience.length}
                  >
                    <div className="space-y-3">
                      {extractedData.experience.map((exp, index) => (
                        <div key={index} className="border-l-2 border-amber-500/40 pl-4 hover:border-amber-400 transition-colors duration-700 ease-out">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-gray-200">{exp.position}</h4>
                              <p className="text-gray-400 text-sm">{exp.company}</p>
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                              {exp.start_date} - {exp.end_date || 'Present'}
                            </span>
                          </div>
                          {exp.description && (
                            <p className="text-gray-400 text-sm mt-2 line-clamp-2">
                              {exp.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Education */}
                {extractedData.education && extractedData.education.length > 0 && (
                  <CollapsibleSection
                    title="Education"
                    icon={<GraduationCap className="w-4 h-4" />}
                    sectionKey="education"
                    count={extractedData.education.length}
                  >
                    <div className="space-y-3">
                      {extractedData.education.map((edu, index) => (
                        <div key={index} className="border-l-2 border-green-500/40 pl-4 hover:border-green-400 transition-colors duration-700 ease-out">
                          <h4 className="font-medium text-gray-200">{edu.degree}</h4>
                          <p className="text-gray-400 text-sm">{edu.institution}</p>
                          {edu.field_of_study && (
                            <p className="text-gray-500 text-sm">{edu.field_of_study}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Languages */}
                {extractedData.languages && extractedData.languages.length > 0 && (
                  <CollapsibleSection
                    title="Languages"
                    icon={<Globe className="w-4 h-4" />}
                    sectionKey="languages"
                    count={extractedData.languages.length}
                  >
                    <div className="flex flex-wrap gap-3">
                      {extractedData.languages.map((lang, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <span className="text-gray-300 text-sm">{lang.language}</span>
                          {lang.proficiency && (
                            <span className="text-xs text-gray-400 bg-white/5 border border-white/10 px-2 py-1 rounded-full">
                              {lang.proficiency}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </CollapsibleSection>
                )}
              </div>

              {/* Footer - always visible */}
              <div className="p-6 border-t border-white/10 space-y-4 bg-gray-900/95 backdrop-blur-xl flex-shrink-0 rounded-b-3xl">
                {userExists ? (
                  <>
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 backdrop-blur-sm">
                      <p className="text-amber-200 text-sm">
                        <strong>Account exists!</strong> Choose to update your profile with this CV data or just log in with existing credentials.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={onJustLogin}
                        className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white rounded-xl transition-all duration-500 ease-out font-medium flex items-center justify-center gap-2 border border-white/10 hover:border-white/20"
                      >
                        <LogIn className="w-4 h-4" />
                        Just Login
                      </button>
                      <button
                        onClick={onUpdateProfile}
                        className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white rounded-xl transition-all duration-500 ease-out font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-amber-500/25"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Update Profile & Login
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 backdrop-blur-sm">
                      <p className="text-green-200 text-sm">
                        <strong>Ready to create account!</strong> Your account will be created with this information and login credentials will be sent to your email.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={onClose}
                        className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white rounded-xl transition-all duration-500 ease-out font-medium border border-white/10 hover:border-white/20"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={onUpdateProfile}
                        className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-white rounded-xl transition-all duration-500 ease-out font-medium flex items-center justify-center gap-2 shadow-lg hover:shadow-green-500/25"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Create Account
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CVConfirmationModal;